import type { CtaTrigger } from 'ProdantixSdk/core/ctas';

/** Calls `fire` once the trigger happens, at most once, and answers a function
 *  that stops waiting. */
export const watchTrigger = (trigger: CtaTrigger, fire: () => void): (() => void) => {
  let done = false;
  const cleanups: (() => void)[] = [];
  const stop = (): void => {
    done = true;
    for (const cleanup of cleanups.splice(0)) {
      cleanup();
    }
  };
  const once = (): void => {
    if (done) {
      return;
    }
    stop();
    fire();
  };
  if (trigger.kind === 'load') {
    queueMicrotask(once);
  } else if (trigger.kind === 'delay') {
    const timer = setTimeout(once, trigger.value * 1000);
    cleanups.push(() => clearTimeout(timer));
  } else if (trigger.kind === 'scroll') {
    const onScroll = (): void => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0 || (window.scrollY / scrollable) * 100 >= trigger.value) {
        once();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));
    onScroll();
  } else if (trigger.kind === 'exit') {
    const onLeave = (event: MouseEvent): void => {
      if (event.relatedTarget === null && event.clientY <= 0) {
        once();
      }
    };
    document.addEventListener('mouseout', onLeave);
    cleanups.push(() => document.removeEventListener('mouseout', onLeave));
  } else {
    const selector = trigger.value;
    const matches = (target: Element): boolean => {
      try {
        return target.closest(selector) !== null;
      } catch {
        return false;
      }
    };
    const onClick = (event: MouseEvent): void => {
      const target = event.target;
      if (target instanceof Element && matches(target)) {
        once();
      }
    };
    document.addEventListener('click', onClick, true);
    cleanups.push(() => document.removeEventListener('click', onClick, true));
  }
  return stop;
};
